import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import Navbar from '../components/Navbar';

/**
 * Safari iOS Dropdown Test Page
 * Tests different approaches to fix Safari dropdown issues
 */

const options = [
  { value: 'option1', label: 'Option 1 - Vehicles' },
  { value: 'option2', label: 'Option 2 - Electronics' },
  { value: 'option3', label: 'Option 3 - Real Estate' },
  { value: 'option4', label: 'Option 4 - Fashion' },
  { value: 'option5', label: 'Option 5 - Sports' },
];

// ============================================
// FIX 1: Z-Index + Transform3D Hardware Acceleration
// ============================================
const DropdownFix1 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div 
      ref={ref}
      className="relative"
      style={{
        position: 'relative',
        zIndex: 50,
        WebkitTransform: 'translate3d(0,0,0)',
        transform: 'translate3d(0,0,0)',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-xl flex items-center justify-between text-left"
      >
        <span>{selected ? options.find(o => o.value === selected)?.label : 'Select option...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
          style={{
            zIndex: 9999,
            WebkitTransform: 'translate3d(0,0,0)',
            transform: 'translate3d(0,0,0)',
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between first:rounded-t-xl last:rounded-b-xl"
            >
              <span>{option.label}</span>
              {selected === option.value && <Check className="w-4 h-4 text-blue-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 2: Overflow Visible + No Container Restrictions
// ============================================
const DropdownFix2 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <div 
      ref={ref}
      style={{
        position: 'relative',
        overflow: 'visible',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-green-500 rounded-xl flex items-center justify-between text-left"
      >
        <span>{selected ? options.find(o => o.value === selected)?.label : 'Select option...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
          style={{
            overflow: 'visible',
            position: 'absolute',
            zIndex: 9999,
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between first:rounded-t-xl last:rounded-b-xl"
            >
              <span>{option.label}</span>
              {selected === option.value && <Check className="w-4 h-4 text-green-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 3: Force Reflow JavaScript Hack
// ============================================
const DropdownFix3 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const forceReflow = () => {
    // Force Safari to recalculate layout
    document.body.style.display = 'table';
    setTimeout(() => {
      document.body.style.display = 'block';
    }, 0);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    
    // Detect Safari and force reflow
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      forceReflow();
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggleDropdown}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-purple-500 rounded-xl flex items-center justify-between text-left"
      >
        <span>{selected ? options.find(o => o.value === selected)?.label : 'Select option...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between first:rounded-t-xl last:rounded-b-xl"
            >
              <span>{option.label}</span>
              {selected === option.value && <Check className="w-4 h-4 text-purple-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 4: onTouchEnd + stopPropagation + Immediate State
// ============================================
const DropdownFix4 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const handleSelect = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected(value);
    setIsOpen(false);
  };

  return (
    <div ref={ref} className="relative" style={{ WebkitTapHighlightColor: 'transparent' }}>
      <button
        type="button"
        onTouchEnd={handleToggle}
        onClick={handleToggle}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-orange-500 rounded-xl flex items-center justify-between text-left touch-manipulation"
        style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
      >
        <span>{selected ? options.find(o => o.value === selected)?.label : 'Select option...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-[9999]"
          style={{ WebkitTransform: 'translateZ(0)' }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onTouchEnd={handleSelect(option.value)}
              onClick={handleSelect(option.value)}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 flex items-center justify-between first:rounded-t-xl last:rounded-b-xl touch-manipulation"
              style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
            >
              <span>{option.label}</span>
              {selected === option.value && <Check className="w-4 h-4 text-orange-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 5: Portal-based Dropdown (renders outside DOM hierarchy)
// ============================================
const DropdownFix5 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-pink-500 rounded-xl flex items-center justify-between text-left"
      >
        <span>{selected ? options.find(o => o.value === selected)?.label : 'Select option...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl"
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 99999,
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between first:rounded-t-xl last:rounded-b-xl"
            >
              <span>{option.label}</span>
              {selected === option.value && <Check className="w-4 h-4 text-pink-500" />}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

// ============================================
// FIX 6: Native Select with Custom Styling
// ============================================
const DropdownFix6 = () => {
  const [selected, setSelected] = useState('');

  return (
    <div className="relative">
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        className="w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 border-cyan-500 rounded-xl appearance-none text-left pr-10"
        style={{
          WebkitAppearance: 'none',
          MozAppearance: 'none',
        }}
      >
        <option value="">Select option...</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none text-gray-500" />
    </div>
  );
};

// ============================================
// Main Test Page
// ============================================
const SafariDropdownTestPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-8 pt-24">
        <h1 className="text-3xl font-bold text-foreground mb-2">Safari Dropdown Test</h1>
        <p className="text-muted-foreground mb-8">Test welke dropdown fix werkt op jouw iOS Safari device</p>
        
        <div className="space-y-8">
          {/* Fix 1 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-blue-500 mb-2">Fix 1: Z-Index + Transform3D</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Hardware acceleratie met translate3d(0,0,0) en hoge z-index
            </p>
            <DropdownFix1 />
          </div>

          {/* Fix 2 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-green-500 mb-2">Fix 2: Overflow Visible</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Alle overflow: hidden verwijderd, explicit visible
            </p>
            <DropdownFix2 />
          </div>

          {/* Fix 3 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-purple-500 mb-2">Fix 3: Force Reflow Hack</h2>
            <p className="text-sm text-muted-foreground mb-4">
              JavaScript truc die Safari dwingt om opnieuw te renderen
            </p>
            <DropdownFix3 />
          </div>

          {/* Fix 4 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-orange-500 mb-2">Fix 4: onTouchEnd + stopPropagation</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Touch events met preventDefault en stopPropagation
            </p>
            <DropdownFix4 />
          </div>

          {/* Fix 5 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-pink-500 mb-2">Fix 5: Portal (Fixed Position)</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Dropdown buiten de DOM hiërarchie met fixed positioning
            </p>
            <DropdownFix5 />
          </div>

          {/* Fix 6 */}
          <div className="p-6 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <h2 className="text-lg font-semibold text-cyan-500 mb-2">Fix 6: Native Select</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Native HTML select element met custom styling (iOS native picker)
            </p>
            <DropdownFix6 />
          </div>
        </div>

        <div className="mt-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <h3 className="font-semibold text-yellow-600 dark:text-yellow-400 mb-2">Test Instructies:</h3>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Open deze pagina op je iOS Safari browser</li>
            <li>Tap op elke dropdown en kijk welke correct opent</li>
            <li>Probeer een optie te selecteren</li>
            <li>Let op of de dropdown sluit en de selectie wordt getoond</li>
            <li>Rapporteer welke fix(es) werken!</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default SafariDropdownTestPage;
